import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { todayStr, fmtDate, OPERATION_TYPES, tourDeparture } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import {
  ClipboardList, CheckCircle2, AlertTriangle, Route as RouteIcon, Clock,
  MapPin, TriangleAlert, Gauge, Plus, Truck,
} from "lucide-react";

const CARDS = [
  { key: "da_pianificare", label: "Da pianificare", icon: ClipboardList, color: "#8C4A32" },
  { key: "pianificate", label: "Pianificate", icon: RouteIcon, color: "#1D4ED8" },
  { key: "completate", label: "Completate", icon: CheckCircle2, color: "#166534" },
  { key: "in_ritardo", label: "In ritardo", icon: AlertTriangle, color: "#991B1B" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isManager = ["responsabile_logistica", "amministratore"].includes(user?.role);
  const [stats, setStats] = useState(null);
  const [toursToday, setToursToday] = useState([]);
  const [toPlan, setToPlan] = useState([]);
  const [myRecent, setMyRecent] = useState([]);

  useEffect(() => {
    api.get(`/dashboard/stats?date=${todayStr()}`).then((r) => setStats(r.data)).catch(() => {});
    if (isManager) {
      api.get(`/tours?date=${todayStr()}`).then((r) => setToursToday(r.data)).catch(() => {});
      api.get("/requests?status=da_pianificare").then((r) => setToPlan(r.data)).catch(() => {});
    } else {
      api.get("/requests").then((r) => setMyRecent(r.data.slice(0, 6))).catch(() => {});
    }
  }, [isManager]);

  return (
    <div className="space-y-6">
      <div className="lm-anim flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-lm-muted font-semibold">Benvenuto, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-display text-3xl sm:text-4xl text-lm-text mt-1">Cruscotto operativo</h1>
          {stats && (
            <p className="text-sm text-lm-muted mt-1" data-testid="dashboard-scope">
              {stats.scope === "mie" ? "Contatori riferiti alle tue richieste" : "Contatori riferiti a tutte le richieste"}
            </p>
          )}
        </div>
        {!isManager && (
          <Button data-testid="dashboard-new-request" onClick={() => nav("/richieste?new=1")} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream h-12 px-6">
            <Plus className="w-5 h-5 mr-2" /> Nuova richiesta
          </Button>
        )}
      </div>

      {isManager && stats?.distance_source && (
        <div className={`text-xs rounded-lg px-3 py-2 border inline-flex items-center gap-2 ${stats.distance_source === "strade_reali" ? "bg-[#EFF7F2] border-[#9AD4AC] text-[#166534]" : "bg-[#FEF8EC] border-[#F3D295] text-[#9A6B00]"}`}>
          <Gauge className="w-4 h-4" />
          {stats.distance_source === "strade_reali"
            ? "Distanze e tempi calcolati su percorsi stradali reali (OpenRouteService)."
            : "Distanze stimate in linea d'aria — ottimizzazione stradale non ancora attiva."}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c, i) => (
          <button key={c.key} data-testid={`stat-${c.key}`} onClick={() => nav("/richieste")}
            className="lm-card p-5 lm-anim text-left hover:shadow-md transition-shadow" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-center justify-between">
              <span className="text-4xl font-display font-bold text-lm-brown tabular-nums">{stats ? stats[c.key] : "—"}</span>
              <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${c.color}18` }}>
                <c.icon className="w-5 h-5" style={{ color: c.color }} />
              </span>
            </div>
            <p className="text-sm text-lm-text2 mt-2 font-medium">{c.label}</p>
          </button>
        ))}
      </div>

      {stats?.missing_info > 0 && (
        <button onClick={() => nav("/richieste")} data-testid="missing-info-banner"
          className="w-full text-left flex items-center gap-3 bg-[#FEF8EC] border border-[#F3D295] rounded-xl px-4 py-3 hover:bg-[#FCEFD3] transition-colors">
          <TriangleAlert className="w-5 h-5 text-[#9A6B00] shrink-0" />
          <span className="text-sm text-[#9A6B00]"><b>{stats.missing_info}</b> richieste con informazioni mancanti da completare per la pianificazione.</span>
        </button>
      )}

      {isManager ? (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="lm-card p-5" data-testid="today-tours">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl text-lm-brown flex items-center gap-2"><Truck className="w-5 h-5" /> Giri di oggi ({toursToday.length})</h2>
              <button onClick={() => nav("/pianificazione")} className="text-sm text-lm-terra font-semibold hover:underline">Pianificazione →</button>
            </div>
            {toursToday.length === 0 ? <p className="text-sm text-lm-muted py-6 text-center">Nessun giro programmato per oggi.</p> : (
              <div className="space-y-2">
                {toursToday.map((t) => (
                  <div key={t.id} data-testid={`today-tour-${t.code}`} className="flex items-center justify-between gap-2 rounded-lg border border-lm-sandsoft px-3 py-2">
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-bold text-lm-brown">{t.code}</p>
                      <p className="text-xs text-lm-muted truncate">{t.driver?.name || "autista n.d."} · {t.stops?.length || 0} tappe · {t.total_km || 0} km</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span data-testid={`today-tour-departure-${t.code}`} className="text-xs font-semibold text-lm-brown bg-lm-sandsoft rounded-md px-2 py-1 whitespace-nowrap flex items-center gap-1"><Clock className="w-3 h-3" />{tourDeparture(t)}</span>
                      <StatusBadge status={t.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lm-card p-5" data-testid="to-plan">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl text-lm-brown flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Da pianificare ({toPlan.length})</h2>
              <button onClick={() => nav("/richieste")} className="text-sm text-lm-terra font-semibold hover:underline">Richieste →</button>
            </div>
            {toPlan.length === 0 ? <p className="text-sm text-lm-muted py-6 text-center">Tutto pianificato. Ottimo lavoro!</p> : (
              <div className="space-y-2">
                {toPlan.slice(0, 6).map((r) => (
                  <div key={r.id} data-testid={`toplan-${r.code}`} className="flex items-center justify-between gap-2 rounded-lg border border-lm-sandsoft px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm text-lm-text truncate"><span className="font-mono font-bold text-lm-brown">{r.code}</span> · {OPERATION_TYPES[r.operation_type]}</p>
                      <p className="text-xs text-lm-muted truncate">{r.supplier_name || r.origin_address || r.dest_address || "—"} · {r.requester_name || ""}</p>
                    </div>
                    <span className="text-xs text-lm-muted whitespace-nowrap"><Clock className="w-3 h-3 inline mr-1" />{r.deadline ? fmtDate(r.deadline) : "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="lm-card p-5" data-testid="my-recent">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl text-lm-brown flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Le mie richieste recenti</h2>
            <button onClick={() => nav("/richieste")} className="text-sm text-lm-terra font-semibold hover:underline">Vedi tutte →</button>
          </div>
          {myRecent.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-lm-muted mb-3">Non hai ancora richieste. Creane una per iniziare.</p>
              <Button data-testid="empty-new-request" onClick={() => nav("/richieste?new=1")} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream"><Plus className="w-4 h-4 mr-2" /> Nuova richiesta</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {myRecent.map((r) => (
                <div key={r.id} data-testid={`myreq-${r.code}`} className="flex items-center justify-between gap-2 rounded-lg border border-lm-sandsoft px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-lm-text truncate"><span className="font-mono font-bold text-lm-brown">{r.code}</span> · {OPERATION_TYPES[r.operation_type]} · {r.supplier_name || "—"}</p>
                    <p className="text-xs text-lm-muted truncate">{r.goods_description || "Merce non descritta"}</p>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
